'use client';

import { useEffect } from 'react';
import { useReducedMotion } from '@/lib/use-reduced-motion';

function parseSamePageHash(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed || trimmed === '#') return null;

  if (trimmed.startsWith('#')) {
    return decodeURIComponent(trimmed.slice(1));
  }

  try {
    const url = new URL(trimmed, window.location.href);
    if (url.pathname !== window.location.pathname || url.search !== window.location.search) {
      return null;
    }
    if (!url.hash || url.hash === '#') return null;
    return decodeURIComponent(url.hash.slice(1));
  } catch {
    return null;
  }
}

function clearUrlHash() {
  if (!window.location.hash) return;
  history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}`);
}

function scrollToAnchor(id: string, smooth: boolean) {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  return true;
}

function waitForScrollEnd(onDone: () => void): () => void {
  let cancelled = false;
  let rafId = 0;
  const scrollRoot = globalThis;

  const cleanup = () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
    scrollRoot.removeEventListener('scrollend', onScrollEnd);
  };

  const finish = () => {
    if (cancelled) return;
    cleanup();
    onDone();
  };

  const onScrollEnd = () => finish();
  const readScrollY = () => scrollRoot.scrollY;

  if ('onscrollend' in scrollRoot) {
    scrollRoot.addEventListener('scrollend', onScrollEnd, { once: true });
    return cleanup;
  }

  let lastY = readScrollY();
  let stableFrames = 0;

  const tick = () => {
    if (cancelled) return;
    const y = readScrollY();
    if (y === lastY) {
      stableFrames += 1;
      if (stableFrames >= 4) {
        finish();
        return;
      }
    } else {
      stableFrames = 0;
      lastY = y;
    }
    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);
  return cleanup;
}

/** Keeps same-page anchor navigation smooth while removing `#anchor` from the URL afterward. */
export function ClearHashAfterScroll() {
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    let pendingCleanup: (() => void) | null = null;

    const scheduleClearAfterScroll = () => {
      pendingCleanup?.();
      pendingCleanup = waitForScrollEnd(() => {
        pendingCleanup = null;
        clearUrlHash();
      });
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-lead-form-trigger="true"]')) return;

      const link = target.closest('a[href]');
      if (!link) return;

      const targetAttr = link.getAttribute('target');
      if (targetAttr && targetAttr !== '_self') return;

      const hashId = parseSamePageHash(link.getAttribute('href') ?? '');
      if (!hashId) return;

      event.preventDefault();
      if (!scrollToAnchor(hashId, !prefersReducedMotion)) return;

      scheduleClearAfterScroll();
    };

    if (window.location.hash) {
      scheduleClearAfterScroll();
    }

    document.addEventListener('click', onClick, true);

    return () => {
      pendingCleanup?.();
      document.removeEventListener('click', onClick, true);
    };
  }, [prefersReducedMotion]);

  return null;
}
